import API_ENDPOINTS from '../config/apiConfig';
import { api } from './api'; // Import the existing api instance from api.js

// Cache for location data
const locationCache = {
  allLocations: null,
  locationsByOrgId: {},
  locationById: {},
  timestamp: null,
  timestampsByOrgId: {}, // per-org timestamps so one org doesn't freshen another's cache
  cacheExpiry: 5 * 60 * 1000 // 5 minutes
};

export const clearLocationCache = (orgId) => {
  if (orgId !== undefined) {
    delete locationCache.locationsByOrgId[orgId];
    delete locationCache.timestampsByOrgId[orgId];
  } else {
    locationCache.allLocations = null;
    locationCache.locationsByOrgId = {};
    locationCache.locationById = {};
    locationCache.timestamp = null;
    locationCache.timestampsByOrgId = {};
  }
};

// Locations API calls
export const getLocations = async () => {
  // Check cache first
  const now = Date.now();
  if (locationCache.allLocations && 
      locationCache.timestamp && 
      (now - locationCache.timestamp) < locationCache.cacheExpiry) {
    return locationCache.allLocations;
  }
  
  try {
    const response = await api.get(API_ENDPOINTS.LOCATIONS);
    
    // Update cache
    locationCache.allLocations = response.data;
    locationCache.timestamp = now;
    
    return response.data;
  } catch (error) {
    console.error('Error in getLocations:', error);
    return locationCache.allLocations || []; // Return cached data or empty array
  }
};

export const getLocationsByOrgId = async (orgId) => {
  try {
    if (!orgId) {
      console.warn('getLocationsByOrgId called without orgId parameter');
      return [];
    }
    
    // Check cache first — only return cached data if it's non-empty and not stale
    const now = Date.now();
    const orgTimestamp = locationCache.timestampsByOrgId[orgId];
    const cachedLocations = locationCache.locationsByOrgId[orgId];
    if (cachedLocations && cachedLocations.length > 0 &&
        orgTimestamp && (now - orgTimestamp) < locationCache.cacheExpiry) {
      return cachedLocations;
    }
    
    // Determine if we have an ID or a code
    let endpoint;
    if (typeof orgId === 'number' || !isNaN(parseInt(orgId))) {
      // It's an ID - use it directly
      endpoint = API_ENDPOINTS.GET_LOCATIONS_BY_ORG(orgId);
    } else {
      // Assume it's a code (string that's not a number)

      endpoint = `${API_ENDPOINTS.LOCATIONS}?orgCode=${orgId}`;
    }
    
    const response = await api.get(endpoint);
    
    if (!response || !response.data) {
      console.warn('getLocationsByOrgId: Empty response from API');
      return [];
    }
    
    const locations = Array.isArray(response.data) ? response.data : [];
    
    // Update cache — only cache non-empty results so stale empty data doesn't get stuck
    if (locations.length > 0) {
      locationCache.locationsByOrgId[orgId] = locations;
      locationCache.timestampsByOrgId[orgId] = now;
    }
    
    // Also cache individual locations
    locations.forEach(location => {
      if (location && location.id) {
        locationCache.locationById[location.id] = location;
      }
    });
    
    return locations;
  } catch (error) {
    console.error('Error in getLocationsByOrgId:', error);
    return locationCache.locationsByOrgId[orgId] || []; // Return cached data or empty array
  }
};

export const getLocationById = async (id) => {
  try {
    if (!id) {
      console.warn('getLocationById called without id parameter');
      return null;
    }
    
    // Check cache first
    const now = Date.now();
    if (locationCache.locationById[id] && 
        locationCache.timestamp && 
        (now - locationCache.timestamp) < locationCache.cacheExpiry) {
      return locationCache.locationById[id];
    }
    
    const response = await api.get(`${API_ENDPOINTS.LOCATIONS}?id=${id}`);
    
    if (!response || !response.data) {
      console.warn('getLocationById: Empty response from API');
      return null;
    }
    
    // Update cache
    locationCache.locationById[id] = response.data;
    locationCache.timestamp = now;
    
    return response.data;
  } catch (error) {
    console.error('Error in getLocationById:', error);
    return locationCache.locationById[id] || null; // Return cached data or null
  }
};

export const createLocation = async (locationData) => {
  try {
    const response = await api.post(API_ENDPOINTS.LOCATIONS, locationData);
    return response.data;
  } catch (error) {
    console.error('Error in createLocation:', error);
    throw error;
  }
};

export const updateLocation = async (locationData) => {
  try {
    const response = await api.put(API_ENDPOINTS.LOCATIONS, locationData);
    return response.data;
  } catch (error) {
    console.error('Error in updateLocation:', error);
    throw error;
  }
};

export const deleteLocation = async (id) => {
  try {
    await api.delete(`${API_ENDPOINTS.LOCATIONS}?id=${id}`);
    return true;
  } catch (error) {
    console.error('Error in deleteLocation:', error);
    throw error;
  }
}; 
// ── Admin → Organizations (throwing variants) ───────────────────────────────
// getLocationsByOrgId() swallows errors and returns [] (other pages rely on
// that). The admin Locations tab needs real error states and fresh data, so
// this one bypasses the cache and throws.
export const fetchLocationsForOrg = async (orgId) => {
  const response = await api.get(API_ENDPOINTS.GET_LOCATIONS_BY_ORG(orgId));
  const locations = Array.isArray(response.data) ? response.data : [];
  if (locations.length > 0) {
    locationCache.locationsByOrgId[orgId] = locations;
    locationCache.timestampsByOrgId[orgId] = Date.now();
  }
  return locations;
};
